import { cva, type VariantProps } from 'class-variance-authority';

import { XStack } from '@/components/ui/layout/layout';
import { useShortcutKeys, type ShortcutId } from '@/features/shortcuts';
import { fmtKeyToSymbol, type KeyName } from '@/lib/key';
import { classList } from '@/utils/cn';

const variants = cva(
  classList(
    'font-mono',
    'text-foreground-subtle',
    'bg-surface-active',
    'flex items-center justify-center',
    'rounded-md smoothing-default',
    'border border-border-strongest',
    'px-1.5'
  ),
  {
    variants: {
      size: {
        default: 'h-6 min-w-6',
        sm: 'h-5 min-w-5',
        xs: 'h-4 min-w-4 px-1',
      },
      textSize: {
        base: 'text-base',
        sm: 'text-sm',
        xs: 'text-xs',
      },
    },
    defaultVariants: {
      size: 'sm',
      textSize: 'sm',
    },
    compoundVariants: [
      {
        size: 'sm',
        className: 'text-xs',
      },
      {
        size: 'xs',
        className: 'text-[10px]',
      },
    ],
  }
);

interface HotkeyLabelProps extends VariantProps<typeof variants> {
  keyName: KeyName;
}

export function HotkeyLabel({ keyName, size, textSize }: HotkeyLabelProps) {
  return (
    <kbd className={classList(variants({ size, textSize }))}>
      {fmtKeyToSymbol(keyName)}
    </kbd>
  );
}

interface HotkeyProps extends Pick<HotkeyLabelProps, 'size' | 'textSize'> {
  hotkey: readonly KeyName[] | ShortcutId;
}

export function Hotkey({ hotkey, size, textSize }: HotkeyProps) {
  const shortcutId = typeof hotkey === 'string' ? hotkey : undefined;
  const shortcutKeys = useShortcutKeys(shortcutId);
  const resolvedKeys = typeof hotkey === 'string' ? shortcutKeys : hotkey;

  if (!resolvedKeys?.length) return null;
  return (
    <XStack className="items-center gap-1">
      {resolvedKeys.map((keyName, index) => (
        <HotkeyLabel
          key={`${keyName}-${index}`}
          keyName={keyName}
          size={size}
          textSize={textSize}
        />
      ))}
    </XStack>
  );
}
